import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Truck, Package } from "lucide-react";
import WebCanvas from "./WebCanvas";
import { scrollToId, eur } from "@/lib/format";

const LINES = [
    { text: "SPIDEY-WEB", cls: "text-white" },
    { text: "NOIR & ROUGE", cls: "text-spidey-red" },
];

const STATS = [
    { icon: Package, label: "2 paires exclusives" },
    { icon: Truck, label: "Livraison offerte dès 50 €" },
    { icon: ShieldCheck, label: "Paiement sécurisé" },
];

export default function Hero() {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 55, damping: 16 });
    const sy = useSpring(my, { stiffness: 55, damping: 16 });
    const px = useTransform(sx, (v) => v * 26);
    const py = useTransform(sy, (v) => v * 26);
    const ix = useTransform(sx, (v) => v * -14);
    const iy = useTransform(sy, (v) => v * -14);

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="hero"
            ref={ref}
            onMouseMove={onMove}
            className="relative min-h-screen flex items-center overflow-hidden noise"
        >
            <WebCanvas />
            <div className="absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-spidey-red/15 blur-[140px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-spidey-blood/25 blur-[120px] pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-16 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center w-full">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-xs font-mono-brand uppercase tracking-[0.3em] text-spidey-neon mb-6"
                    >
                        Drip4saisons — Drop 2026
                    </motion.p>

                    <h1 className="font-display uppercase leading-[0.92] text-[clamp(3rem,9vw,6.5rem)] tracking-tight">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.cls}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 0.95,
                                        delay: 0.18 + i * 0.13,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65 }}
                        className="mt-6 max-w-lg text-zinc-400 leading-relaxed"
                    >
                        Deux paires de Spider-Web noir & rouge — ultra-légères,
                        confortables, stock très limité.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.78 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={() => scrollToId("products")}
                            className="group bg-spidey-red hover:bg-spidey-neon text-white font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-full transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(230,0,38,0.55)]"
                            data-testid="hero-explore-collection-button"
                        >
                            Découvrir la collection
                        </button>
                        <span className="font-mono-brand text-sm text-zinc-400">
                            dès <span className="text-white font-bold">{eur(49.99)}</span>
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
                    >
                        {STATS.map((s) => (
                            <span key={s.label} className="flex items-center gap-2 text-xs font-mono-brand uppercase tracking-wider text-zinc-500">
                                <s.icon size={15} className="text-spidey-red" />
                                {s.label}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    style={{ x: px, y: py }}
                    className="relative"
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                    <motion.div style={{ x: ix, y: iy }} className="relative">
                        <div className="absolute -inset-6 bg-spidey-red/25 blur-3xl rounded-full animate-pulse-glow" />
                        <img
                            src="https://customer-assets-wrfwihn1.emergentagent.net/job_web-crocs-shop/artifacts/45ebeeebfb462e83_IMG_0186.jpeg"
                            alt="Collection Clogs Spidey-Web rouges — Drop 2026"
                            className="relative w-full aspect-[4/5] object-cover rounded-2xl border border-zinc-800"
                            style={{ clipPath: "polygon(0 0, 100% 4%, 96% 100%, 4% 96%)" }}
                            data-testid="hero-product-image"
                        />
                        <div className="absolute top-5 -left-4 bg-spidey-red text-white font-mono-brand text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full glow-red">
                            Drop 2026
                        </div>
                        <div className="absolute bottom-5 -right-3 bg-black/85 backdrop-blur border border-zinc-800 text-white font-display text-lg px-5 py-3 rounded-xl">
                            Édition limitée
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.button
                onClick={() => scrollToId("products")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, y: [0, 8, 0] }}
                transition={{
                    opacity: { delay: 1.4, duration: 0.6 },
                    y: { repeat: Infinity, duration: 1.8, ease: "easeInOut" },
                }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-zinc-500 hover:text-spidey-neon transition-colors"
                aria-label="Descendre vers la collection"
            >
                <ArrowDown size={22} />
            </motion.button>
        </section>
    );
}
